import { useState, useContext, useEffect } from 'react';
import MercuryContext from 'ConnectionManager/mercuryContext';
import getDocument from 'builder/Knowledge/queries/getDocument';
import simplifyDocumentQueryResult from 'builder/Knowledge/helpers/simplifyDocumentQueryResult';
import { childAssociations } from 'builder/Knowledge/associations/itemAssociationFunctions';

/**
 * Loads a documentItem and it's child documentItems. If no documentId or documentItemId is
 * provided, then the data isn't fetched.
 * @param {string} workspaceId The current workspace (Used for auth - has no impact on the result)
 * @param {string} documentId
 * @param {string} documentItemId
 * @returns {{ loading: boolean, error: Object, data: Object }}
 * A state object containing a loading, error, and the fetched data.
 */
export default function useDocumentItem(workspaceId, documentId, documentItemId) {
  const [state, setState] = useState({ data: {} });
  const { apolloClient } = useContext(MercuryContext);

  useEffect(() => {
    if (documentId) {
      setState({
        loading: true,
        error: null,
        data: {},
      });

      apolloClient.query({
        query: getDocument,
        fetchPolicy: 'cache-first',
        variables: {
          workspaceId,
          documentId,
        },
      })
        .then((response) => {
          const competencyDocument = simplifyDocumentQueryResult(response.data);
          const parentDocumentItem = competencyDocument.items.find(({ id }) => documentItemId === id);
          const childItemIds = childAssociations(parentDocumentItem).map((asc) => asc.originItemId);
          const childDocumentItems = competencyDocument.items.filter(({ id }) => childItemIds.includes(id));

          setState({
            loading: false,
            error: null,
            data: {
              parentDocumentItem,
              childDocumentItems,
              documentItems: [parentDocumentItem, ...childDocumentItems],
            },
          });
        })
        .catch((error) => {
          setState({
            loading: false,
            error,
            data: {},
          });
        });
    }
  }, [apolloClient, workspaceId, documentId, documentItemId]);

  return state;
}
